<template>
  <section id="experience" class="light-section">
    <div class="container-fluid">
      <h1 class="section-header">{{ heading }}</h1>
      <p class="section-subheading">My professional journey and career achievements</p>
      
      <!-- Timeline Layout -->
      <div class="experience-timeline">
        <div 
          v-for="(job, index) in jobs" 
          :key="index"
          class="timeline-item"
          :class="{ 'right': index % 2 !== 0 }"
        >
          <div class="timeline-content">
            <div class="job-header">
              <div class="company-logo" v-if="job.logo">
                <img :src="require(`../../assets/images/companies/${job.logo}`)" rel="preload"/>
              </div>
              <div class="job-title-container">
                <h3>{{ job.title }}</h3>
                <a :href="job.website" target="_blank"><div class="company-name">{{ job.company }}</div></a>
                <div class="job-duration">
                  <i class="far fa-calendar-alt"></i> {{ job.period }}
                </div>
                
                <div class="job-location">
                  <i class="fas fa-map-marker-alt"></i> {{ job.location }}
                </div>
              </div>
            </div>
            
            <div class="job-industry">
              <span class="industry-tag">{{ job.industry }}</span>
            </div>
            
            <div class="job-description">
              <p>{{ job.description }}</p>
            </div>
            
            <div class="key-achievements">
              <h4>Key Achievements</h4>
              <ul>
                <li v-for="(achievement, i) in job.achievements" :key="i">
                  {{ achievement }}
                </li>
              </ul>
            </div>
            
            <div v-if="job.skills && job.skills.length" class="job-skills">
              <h4>Skills Utilized</h4>
              <div class="skill-tags">
                <span 
                  v-for="(skill, i) in job.skills" 
                  :key="i"
                  class="skill-tag"
                >
                  {{ skill }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Arrow />
  </section>
</template>

<script>
import data from "../../data/data.json";
import Arrow from "../components/Arrow.vue";

export default {
  name: "WorkExperience",
  components: {
    Arrow,
  },
  data() {
    return {
      heading: data.main.headings.experience || "Work Experience",
      jobs: data.experience.jobs || []
    };
  }
};
</script>

<style lang="scss" scoped>
// Define variables inline for dark theme
$primary-color: #a334db;
$light-color: #ffffff;
$dark-color: #333333;
$text-color: #e0e0e0;
$background-color: #222222;
$border-color: #444444;
$highlight-color: rgba(163, 52, 219, 0.8);
$spacer-lg: 3rem;

#experience {
  background-color: $background-color;
  color: $text-color;
  
  .container-fluid {
    max-width: 1400px;
    margin: $spacer-lg auto;
    padding-left: 2rem;
    padding-right: 2rem;
  }
  
  .section-header {
    color: $light-color;
    text-align: center;
  }
  
  .section-subheading {
    text-align: center;
    color: #aaaaaa;
    margin-bottom: 2rem;
  }
  
  .experience-timeline {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem 0;
    
    &::before {
      content: '';
      position: absolute;
      width: 4px;
      background-color: $primary-color;
      top: 0;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
    }
  }
  
  .timeline-item {
    position: relative;
    width: 50%;
    padding-bottom: -15rem; /* Reduced from 3rem to 1.5rem */
    padding-right: 2.5rem;
    
    &::after {
      content: '';
      position: absolute;
      width: 20px;
      height: 20px;
      background-color: $primary-color;
      border: 4px solid $background-color;
      border-radius: 50%;
      top: 15px;
      right: -10px;
      z-index: 1;
    }
    
    &.right {
      left: 50%;
      padding-left: 2.5rem;
      padding-right: 0;
      margin-top: -15rem;
      
      &::after {
        left: -10px;
        right: auto;
      }
    }
    
    .timeline-content {
      background-color: rgba(25, 25, 25, 0.8);
      padding: 1.5rem;
      border-radius: 8px;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      position: relative;
      
      &::before {
        content: '';
        position: absolute;
        border-style: solid;
        border-width: 10px 0 10px 10px;
        border-color: transparent transparent transparent rgba(25, 25, 25, 0.8);
        top: 15px;
        right: -10px;
      }
      
      .timeline-item.right & {
        &::before {
          left: -10px;
          border-width: 10px 10px 10px 0;
          border-color: transparent rgba(25, 25, 25, 0.8) transparent transparent;
        }
      }
    }
  }
  
  .job-header {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    
    .company-logo {
      margin-left: 20px;
      margin-top: 20px;
      width: 150px;
      height: 150px;
      margin-right: 3rem;
      background-color: white;
      border-radius: 4px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      
      img {
        max-width: 100%;
        max-height: 100%;
      }
    }
    
    .job-title-container {
      flex: 1;
      
      h3 {
        font-size: 1.2rem;
        margin: 0 0 0.2rem 0;
        color: $light-color;
      }
      
      .company-name {
        font-size: 1rem;
        font-weight: 600;
        color: $primary-color;
      }
      
      .job-duration {
        font-size: 0.85rem;
        color: #aaaaaa;
        margin-top: 0.2rem;
        
        i {
          margin-right: 0.3rem;
        }
      }
    }
  }
  
  .job-location {
    font-size: 0.85rem;
    color: #aaaaaa;
    margin-bottom: 0.75rem;
    
    i {
      margin-right: 0.3rem;
      color: $primary-color;
    }
  }
  
  .job-industry {
    margin-bottom: 1rem;
    
    .industry-tag {
      display: inline-block;
      background-color: rgba($primary-color, 0.2);
      color: $primary-color;
      padding: 0.2rem 0.75rem;
      border-radius: 15px;
      font-size: 0.8rem;
      font-weight: 500;
    }
  }
  
  .job-description {
    margin-bottom: 1rem;
    line-height: 1.5;
    
    p {
      color: $text-color;
      font-size: 0.95rem;
    }
  }
  
  .key-achievements {
    margin-bottom: 1rem;
    
    h4 {
      font-size: 1rem;
      color: $light-color;
      margin-bottom: 0.5rem;
    }
    
    ul {
      list-style-type: none;
      padding-left: 0.5rem;
      
      li {
        position: relative;
        padding-left: 1.5rem;
        margin-bottom: 0.5rem;
        font-size: 0.9rem;
        line-height: 1.5;
        
        &::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: $primary-color;
          font-weight: bold;
        }
      }
    }
  }
  
  .job-skills {
    h4 {
      font-size: 1rem;
      color: $light-color;
      margin-bottom: 0.5rem;
    }
    
    .skill-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      justify-content: center;
      
      .skill-tag {
        background-color: rgba(255, 255, 255, 0.1);
        color: $light-color;
        padding: 0.2rem 0.6rem;
        border-radius: 15px;
        font-size: 0.8rem;
        transition: all 0.2s ease;
        
        &:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 992px) {
  #experience {
    .experience-timeline {
      &::before {
        left: 40px;
      }
    }
    
    .timeline-item {
      width: 100%;
      padding-left: 80px;
      padding-right: 0;
      margin-bottom: 1rem;
      
      &::after {
        left: 30px;
        right: auto;
      }
      
      &.right {
        left: 0;
        padding-left: 80px;
        margin-top: 0;
      }
      
      .timeline-content {
        &::before {
          left: -10px;
          border-width: 10px 10px 10px 0;
          border-color: transparent rgba(25, 25, 25, 0.8) transparent transparent;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  #experience {
    .container-fluid {
      padding-left: 1rem;
      padding-right: 1rem;
    }
  }
}
</style>